/**
 * The ONE global transfer subscription (mounted in `App.tsx` beside
 * `useFileDrop()` — jobs outlive tabs). Hydrates the store from
 * `transfer-list` so `interrupted` jobs of a previous run appear, fans every
 * `transfer-progress` snapshot into the store, and reacts to what the store
 * reports: a terminal transition → toast + `feature:storage_transfer` (once per
 * job per terminal state), a `SourceChanged` notice → warning toast (once per
 * attempt). No filesystem access here — everything is read from the snapshot.
 */
import { useEffect } from 'react';
import type { TransferJob, TransferProgressEvent } from '../../../types/azureStorageTransfers';
import { useTransfersStore, type TransferHydrateMode } from '../store/transfersStore';
import { getTransfersApi, listTransfers, trackTransferEvent } from '../services/transfersApi';
import { showTransferNoticeToast, showTransferToast, SOURCE_CHANGED_CODE } from '../components/TransferToast';

const MB = 1024 * 1024;

export function bytesBucket(bytes: number | undefined): string {
  if (bytes === undefined) return 'unknown';
  if (bytes < MB) return '<1MB';
  if (bytes < 100 * MB) return '1-100MB';
  if (bytes < 1024 * MB) return '100MB-1GB';
  return '>1GB';
}

export function itemsBucket(items: number | undefined): string {
  if (items === undefined) return 'unknown';
  if (items <= 1) return '1';
  if (items <= 10) return '2-10';
  if (items <= 100) return '11-100';
  return '>100';
}

function trackTerminal(job: TransferJob): void {
  trackTransferEvent('feature:storage_transfer', {
    direction: job.kind,
    scope: job.scope,
    itemsBucket: itemsBucket(job.itemsTotal),
    bytesBucket: bytesBucket(job.bytesTotal ?? job.bytesDone),
    outcome: job.state,
    source: job.origin,
    // Review E F11: the UI surface the store noted at start (toolbar / row / keyboard / drop / tree, 'restored' for a
    // job loaded at startup); main's MCP origin always wins it, so renderer bookkeeping never relabels an agent's job.
    surface: job.origin === 'mcp' ? 'mcp' : useTransfersStore.getState().sources[job.id] ?? 'unknown',
    backend: job.backend,
  });
}

/** Exported for the hook's unit tests; the hook is the only production caller. */
export function handleTransferEvent(event: TransferProgressEvent): void {
  const store = useTransfersStore.getState();
  const result = store.applyEvent(event);
  if (result.kind === 'terminal') {
    showTransferToast(event);
    trackTerminal(event.job);
  }
  if (event.lastError?.code === SOURCE_CHANGED_CODE) {
    const key = `${event.lastError.code}@${event.job.attempt}`;
    if (useTransfersStore.getState().noticeKeys[event.job.id] !== key) {
      store.markNoticeSeen(event.job.id, key);
      showTransferNoticeToast(event);
    }
  }
}

/**
 * Re-reads `transfer-list` (after an `invalid_input` refusal, or on demand); the list is the truth unless `mode` is
 * `'initial'`. Last UI/UX pass (Codex Z2): the read's state is kept (`historyRead`) — a failure is shown with Retry,
 * never as an empty history, and the rows already listed stay.
 */
export async function refreshTransfers(mode: TransferHydrateMode = 'refresh'): Promise<void> {
  const store = useTransfersStore.getState();
  store.setHistoryRead({ state: 'loading' });
  const result = await listTransfers().catch((error: unknown) => ({ ok: false as const, error: { message: error instanceof Error ? error.message : String(error) } }));
  if (result.ok) {
    store.hydrate(result.value.jobs, mode);
    store.setHistoryRead({ state: 'ready' });
  } else {
    store.setHistoryRead({ state: 'failed', message: result.error.message });
  }
}

export function useTransferEvents(): void {
  useEffect(() => {
    const api = getTransfersApi();
    if (!api) return;
    const unsubscribe = api.onTransferProgress(handleTransferEvent);
    void refreshTransfers('initial');
    return unsubscribe;
  }, []);
}
